/**
 * PreloadManager Component
 * Manages texture preloading inside the Canvas
 */

import { useState, useEffect, useMemo, useRef } from 'react'
import lodConfig from './earth/config/lodConfig.json'
import { usePreloadAllLODs } from './earth/hooks/usePreloadAllLODs'
import { usePreloadAllLODs as useMoonPreloadAllLODs } from './moon/hooks/usePreloadAllLODs'
import { getDeviceInfo } from '../utils/deviceDetection'

export function PreloadManager({ children, onProgress }) {
  // Detect device and orientation (memoized)
  const deviceInfo = useMemo(() => getDeviceInfo(), [])
  const isMobile = deviceInfo.isMobile
  
  // Get appropriate LOD config for device and filter out disabled LODs (memoized)
  const lodLevels = useMemo(() => {
    const allLods = isMobile ? lodConfig.mobile.lodLevels : lodConfig.desktop.lodLevels
    return allLods.filter(lod => lod.enabled !== false) // Default to enabled if not specified
  }, [isMobile])

  // Preload Earth LOD levels
  const lodNamesToPreload = useMemo(() => lodLevels.map(lod => lod.name), [lodLevels])
  const { isPreloading: earthIsPreloading, preloadedCount: earthPreloadedCount, currentLoadingItem: earthLoadingItem } = usePreloadAllLODs(lodNamesToPreload, true)
  
  // Preload Moon LOD levels
  const { isPreloading: moonIsPreloading, preloadedCount: moonPreloadedCount, currentLoadingItem: moonLoadingItem } = useMoonPreloadAllLODs(lodNamesToPreload, true)

  // Calculate total textures to preload
  // Earth: Each LOD has 5 texture types (diffuse, normal, specular, nightmap, clouds)
  // Moon: Each LOD has 1 texture type
  const totalEarthTextures = lodNamesToPreload.length * 5
  const totalMoonTextures = lodNamesToPreload.length
  const totalTextures = totalEarthTextures + totalMoonTextures
  const totalPreloaded = earthPreloadedCount + moonPreloadedCount
  const isPreloading = earthIsPreloading || moonIsPreloading
  
  // Determine what's currently loading
  const currentLoadingPhase = isPreloading ? 'LOADING TEXTURES' : 'LOADING MODELS'

  // Calculate progress percentage and notify parent
  const prevProgressRef = useRef({ progress: 0, complete: false, phase: '' })
  
  useEffect(() => {
    const progress = totalTextures > 0 ? (totalPreloaded / totalTextures) * 100 : 0
    const complete = !isPreloading && totalPreloaded === totalTextures
    
    // Only call onProgress if values actually changed
    const prev = prevProgressRef.current
    if (prev.progress !== progress || prev.complete !== complete || prev.phase !== currentLoadingPhase) {
      prevProgressRef.current = { progress, complete, phase: currentLoadingPhase }
      if (onProgress) {
        onProgress(progress, complete, currentLoadingPhase)
      }
    }
  }, [totalPreloaded, totalTextures, isPreloading, currentLoadingPhase, onProgress])

  // Don't render children until preloading is complete
  if (isPreloading) {
    return null
  }

  return children
}

export default PreloadManager
